import React, { useState } from 'react';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import axios from 'axios';
import { toast } from 'react-toastify';

const DefaultRestaurantSelector = () => {
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurantsContext();
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRestaurantId, setPendingRestaurantId] = useState(null);

  const handleRestaurantChange = (restaurantId) => {
    // Trouver le restaurant actuel par défaut
    const currentDefault = restaurants.find(r => r.is_default);
    
    // Si on change le restaurant par défaut, afficher une confirmation
    if (currentDefault && currentDefault.id !== restaurantId) {
      setShowConfirmation(true);
      setPendingRestaurantId(restaurantId);
    } else {
      // Sinon, procéder directement
      updateDefaultRestaurant(restaurantId);
    }
  };

  const updateDefaultRestaurant = async (restaurantId) => {
    try {
      setLoading(true);
      
      // Trouver le restaurant sélectionné
      const restaurant = restaurants.find((r) => r.id === restaurantId);
      
      if (!restaurant) {
        toast.error('Restaurant non trouvé');
        return;
      }
      
      // Mettre à jour le restaurant sélectionné localement
      setSelectedRestaurant(restaurant);
      
      // Mettre à jour le restaurant par défaut sur le serveur
      await axios.put(`/api/back-office/restaurants/${restaurantId}/set-default`);
      
      toast.success('Restaurant par défaut mis à jour avec succès');
      
      // Réinitialiser l'état de confirmation
      setShowConfirmation(false);
      setPendingRestaurantId(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du restaurant par défaut:', error);
      toast.error('Erreur lors de la mise à jour du restaurant par défaut');
    } finally {
      setLoading(false);
    }
  };

  const cancelChange = () => {
    setShowConfirmation(false);
    setPendingRestaurantId(null);
  };

  return (
    <div className="default-restaurant-selector p-4 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4">Sélectionner le restaurant par défaut</h2>
      
      {showConfirmation && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md">
          <p className="font-semibold mb-2">Attention !</p>
          <p className="mb-2">
            Changer le restaurant par défaut peut affecter les produits associés aux restaurants.
            Si vous désactivez ultérieurement l'ancien restaurant par défaut, ses produits seront automatiquement copiés vers le nouveau restaurant par défaut.
          </p>
          <p className="mb-2">
            <strong>Note importante pour les restaurants en mode "No Limit" :</strong> Si le nouveau restaurant par défaut est en mode "No Limit", seuls les produits qui n'existent pas déjà dans ce restaurant seront importés pour éviter les duplications.
          </p>
          <div className="flex space-x-2 mt-3">
            <button
              onClick={() => updateDefaultRestaurant(pendingRestaurantId)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? 'Chargement...' : 'Confirmer'}
            </button>
            <button
              onClick={cancelChange}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              disabled={loading}
            >
              Annuler
            </button>
          </div>
        </div>
      )}
      
      {restaurants.length === 0 ? (
        <p>Aucun restaurant disponible</p>
      ) : (
        <div className="space-y-3">
          {restaurants.map((restaurant) => (
            <div key={restaurant.id} className="flex items-center space-x-2">
              <input
                type="radio"
                id={`default-restaurant-${restaurant.id}`}
                name="default-restaurant"
                value={restaurant.id}
                checked={restaurant.is_default}
                onChange={() => handleRestaurantChange(restaurant.id)}
                disabled={loading || showConfirmation}
                className="w-4 h-4 text-blue-600"
              />
              <label 
                htmlFor={`default-restaurant-${restaurant.id}`}
                className={`flex-1 cursor-pointer ${restaurant.is_default ? 'font-semibold' : ''}`}
              >
                {restaurant.name}
                {restaurant.is_default && <span className="ml-2 text-sm text-green-600">(Par défaut)</span>}
                {!restaurant.is_enabled && <span className="ml-2 text-sm text-red-600">(Désactivé)</span>}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DefaultRestaurantSelector; 